import { JobSeekingStatus } from "@nimble/core-client/types";
import {
  Alert,
  AlertDescription,
} from "@nimble/react-requests/components/ui/alert";
import { useIsMobile } from "@nimble/react-state-hooks/use-mobile";
import { Lock } from "lucide-react";
import * as React from "react";
import { useCallback, useState } from "react";

import { LoaderButtonProps } from "@/components/shared/button-loader/button-loader";
import {
  JobseekingStatusDrawer,
  JobseekingStatusItem,
  JobSeekingStatusOption,
  jobSeekingStatusOptions,
  JobseekingStatusPopover,
  TriggerableComponentPropsType,
  TriggerProps,
} from "@/components/shared/job-seeking-status-dropdown/job-seeking-status-components";
import { dormantSeasonTestIds } from "@/lib/data-test-ids/components.data-test-ids";
import { profilePreferencesTestIds } from "@/lib/data-test-ids/profile.data-test-ids";
import { getMatchStatusDormantNotice } from "@/lib/dormant-season/match-status-copy";

export function JobseekingStatusDropdownView({
  onChange,
  selectedOption,
  value,
  TriggerableComponent,
  TriggerComponent,
  isOpen,
  onOpenChange,
  matchesDormantNotice,
  ...props
}: {
  onChange: (value: JobSeekingStatus) => void;
  selectedOption: JobSeekingStatusOption;
  value: JobSeekingStatus;
  TriggerComponent: React.ComponentType<TriggerProps>;
  TriggerableComponent: React.ComponentType<TriggerableComponentPropsType>;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  matchesDormantNotice?: string;
}) {
  const content = (
    <div className="w-full">
      <div className="px-4 py-2.5 border-b border-border">
        <h2 className="text-sm font-bold text-foreground">Job search status</h2>
      </div>
      {matchesDormantNotice && (
        <Alert
          className="border-transparent bg-success-soft rounded-none"
          data-testid={dormantSeasonTestIds.MATCH_STATUS_NOTICE}
        >
          <Lock className="text-success" />
          <AlertDescription>{matchesDormantNotice}</AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col py-1">
        {Object.values(jobSeekingStatusOptions).map((option) => (
          <JobseekingStatusItem
            key={option.value}
            value={value}
            option={option}
            onChange={onChange}
            dataTestId={option.dataTestId}
            disabled={
              !!matchesDormantNotice &&
              option.value === JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES
            }
          />
        ))}
      </div>
    </div>
  );

  return (
    <TriggerableComponent
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      content={content}
      triggerButton={
        <TriggerComponent
          label={selectedOption.shortLabel}
          icon={<selectedOption.Icon className="w-5 h-5" />}
          data-testid={profilePreferencesTestIds.JOB_SEEKING_STATUS}
          {...props}
        />
      }
    />
  );
}

type JobseekingStatusDropdownProps = {
  value: JobSeekingStatus;
  onChange: (value: JobSeekingStatus) => void;
  TriggerComponent: React.ComponentType<TriggerProps>;
} & Omit<LoaderButtonProps, "value" | "onChange">;

export function JobseekingStatusDropdown({
  value,
  onChange,
  ...props
}: JobseekingStatusDropdownProps) {
  const isMobile = useIsMobile();
  const selectedOption =
    jobSeekingStatusOptions[value] ||
    jobSeekingStatusOptions[JobSeekingStatus.CLOSED_TO_WORK];

  const [isOpen, setOpen] = useState(false);

  const matchesDormantNotice = getMatchStatusDormantNotice(new Date());

  const onChangeProxy = useCallback(
    (newStatus: JobSeekingStatus) => {
      onChange(newStatus);
      setOpen(false);
    },
    [setOpen, onChange],
  );

  return (
    <JobseekingStatusDropdownView
      selectedOption={selectedOption}
      value={value}
      onChange={onChangeProxy}
      isOpen={isOpen}
      onOpenChange={setOpen}
      matchesDormantNotice={matchesDormantNotice}
      TriggerableComponent={
        isMobile ? JobseekingStatusDrawer : JobseekingStatusPopover
      }
      {...props}
    />
  );
}
